import { Component, OnInit, inject, HostListener, signal, viewChild, ElementRef, DestroyRef } from "@angular/core";
import { TranslateService, TranslatePipe } from "@ngx-translate/core";
import { AppSettingsService, DashboardsService, CardViewService, AnalyticsService } from "./../shared";
import { FirmsService } from "@shared";
import { ApplicationType, isJobsBilling } from "@app/views-interfaces/ApplicationType.enum";
import { IDashboardModel } from "@app/views-interfaces";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { RouterLink } from "@angular/router";
import { JobsFilterMenuComponent } from "../jobs-filter-menu/jobs-filter-menu.component";
import { FilterMenuComponent } from "../filter-menu/filter-menu.component";
import { SortMenuComponent } from "../sort-menu/sort-menu.component";
import { DisplayComponent } from "./display/display.component";
import { JumpstartComponentsModule } from "@wk/components-v3-angular17";
import { AuthService } from "@shared/auth";

type ClickType = "app" | "year" | "dashboard" | null;

interface DashboardOption {
    appTitle: string;
    flavor: string;
    year?: string;
    dashboard: IDashboardModel;
    isCurrent: boolean;
}

@Component({
    selector: "wk-navbar",
    templateUrl: "./navbar.component.html",
    styleUrl: "./navbar.component.scss",
    host: { class: "wk-navbar navbar" },
    imports: [RouterLink, JobsFilterMenuComponent, FilterMenuComponent, SortMenuComponent, DisplayComponent, TranslatePipe, JumpstartComponentsModule],
})
export class NavBarComponent implements OnInit {
    private readonly analyticsService = inject(AnalyticsService);
    private readonly firmsService = inject(FirmsService);
    private readonly appSettingsService = inject(AppSettingsService);
    protected readonly cardViewService = inject(CardViewService);
    private readonly dashboardsService = inject(DashboardsService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly translateService = inject(TranslateService);
    protected readonly authService = inject(AuthService);

    navbarBreadcrumbYearButton = viewChild<ElementRef<HTMLElement>>("wkNavbarBreadcrumbButtonYear");
    navbarBreadcrumbDashboardButton = viewChild<ElementRef<HTMLElement>>("wkNavbarBreadcrumbButtonDashboard");

    current: DashboardOption | null = null;
    allAvailable: DashboardOption[] = [];
    allApplications = null;
    appYears = null;
    appYearDashboards = null;
    selectedApp = null;
    selectedYear = null;
    public breadcrumbVisibility_Title = false;
    public breadcrumbVisibility_Years = false;
    public breadcrumbVisibility_DashboardNames = false;
    protected submenuWidth_Application = signal<number | null>(null);
    protected submenuWidth_Years = signal<number | null>(null);

    currentDashboard: IDashboardModel;

    private shouldTrackDashboardSwitch = false;
    public isJobsBillingDashboard = false;

    protected iconLabelKey(flavor: string | null | undefined): string | null {
        switch (flavor) {
            case "T1":
                return "Navbar.T1IconLabel";
            case "J&B":
                return "Navbar.JobsBillingIconLabel";
            default:
                return null;
        }
    }

    protected toggleAppMenuClicked = false;
    protected toggleAppMenuClickedBy: ClickType = null;
    private readonly order = ["Taxprep", "iFirm Taxprep"];

    async ngOnInit() {
        this.currentDashboard = await this.dashboardsService.getCurrentDashboard(false);
        this.init();

        this.dashboardsService.currentDashboardChanged$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(async () => {
            this.currentDashboard = await this.dashboardsService.getCurrentDashboard(false);
            this.init();

            if (this.shouldTrackDashboardSwitch) {
                this.analyticsService.track("Dashboard switch"); // TODO not sure why it would be here to track
                this.shouldTrackDashboardSwitch = false;
            }
        });
    }

    async init() {
        this.isJobsBillingDashboard = isJobsBilling(this.currentDashboard?.ApplicationType);
        const accessibleApplications = await this.firmsService.getAccessibleApplications();
        const accessibleDashboards = (await this.dashboardsService.getAccessibleDashboardsList()) ?? [];
        this.allAvailable = accessibleDashboards
            .map((dashboard) => {
                const appKey = accessibleApplications.find((app) => app.Value.find((it) => it.Id === dashboard.Id)).Key;
                const appParts = appKey.split(" ");

                if (dashboard.ApplicationType === ApplicationType.JobsBilling) {
                    const appTitle = this.translateService.instant("Dashboard.jobsAndBilling.Title");
                    if (!this.order.includes(appTitle)) {
                        this.order.push(appTitle);
                    }

                    return {
                        appTitle: appTitle,
                        flavor: "J&B",
                        dashboard: dashboard,
                        isCurrent: dashboard.Id === this.currentDashboard.Id,
                    };
                }

                if (appParts.length === 4 && appParts[0] === "iFirm") {
                    appParts[0] += " " + appParts[1];
                    appParts.splice(1, 1);
                }
                return {
                    appTitle: appParts[0],
                    flavor: appParts[1],
                    year: appParts[2],
                    dashboard: dashboard,
                    isCurrent: dashboard.Id === this.currentDashboard.Id,
                };
            })
            .sort(
                (a, b) =>
                    Number.parseInt(b.year ?? "") - Number.parseInt(a.year ?? "") ||
                    a.appTitle.localeCompare(b.appTitle) ||
                    a.dashboard.Title.localeCompare(b.dashboard.Title),
            );
        this.current = this.allAvailable.find((it) => it.isCurrent) ?? null;
    }

    openHelp(): void {
        this.analyticsService.track("User needs help");
        const langShortCode = this.translateService.getCurrentLang().split("-");
        window.open(`${this.appSettingsService.getSetting("Help_URL")}/${langShortCode[0]}/content/cch_ifirm/ifirm_-_home.html`, "_blank");
    }

    toggleAppMenu(appTitle, flavor, year, type: ClickType) {
        this.toggleAppMenuClicked = true;
        this.breadcrumbVisibility_Years = false;
        this.breadcrumbVisibility_Title = false;
        this.breadcrumbVisibility_DashboardNames = false;
        const savedClickedType = this.toggleAppMenuClickedBy;
        this.toggleAppMenuClickedBy = type;
        this.setBreadcrumbPosition();
        if (this.allApplications) {
            this.closeAllSubMenus(savedClickedType === type);
        }
        if (type === savedClickedType) return;
        if (!this.current) return;

        let index = 0;
        const tempAllavailable = this.allAvailable
            .map((it) => {
                return {
                    appTitle: it.appTitle,
                    flavor: it.flavor,
                };
            })
            .reduce(function (acc, it) {
                if (!acc.some((it2) => it2.appTitle === it.appTitle && it2.flavor === it.flavor)) {
                    acc.push(it);
                }
                return acc;
            }, [])
            .map((app) => {
                return {
                    appTitle: app.appTitle,
                    flavor: app.flavor,
                    isCurrent: this.current?.appTitle === app.appTitle && this.current?.flavor === app.flavor,
                    index: index++,
                };
            });

        this.allApplications = tempAllavailable
            .sort((a, b) => this.order.indexOf(a.appTitle) - this.order.indexOf(b.appTitle))
            .map((app, idx) => ({ ...app, index: idx }));

        if (appTitle && flavor) {
            this.onAppSelected(
                null,
                this.allApplications.find((app) => app.appTitle === appTitle && app.flavor === flavor),
                true,
            );
        }

        if (year) {
            this.onYearSelected(
                null,
                this.appYears.find((app) => app.year === year),
                true,
            );
        }
    }

    onAppSelected(event, app, isSelected = false) {
        let index = 0;

        const current = this.current;
        this.appYears = this.allAvailable
            .filter((it) => it.appTitle === app.appTitle && it.flavor === app.flavor && app.flavor !== "J&B")
            .reduce(function (acc, it) {
                if (!acc.some((it2) => it2.year === it.year)) {
                    acc.push({
                        year: it.year,
                        isCurrent: app.isCurrent && it.year === current?.year,
                        index: index++,
                    });
                }
                return acc;
            }, []);

        this.selectedApp = app;
        this.selectedYear = null;
        this.appYearDashboards = null;

        if (isSelected) {
            this.breadcrumbVisibility_Years = true;
        }

        event?.stopPropagation();

        if (app.flavor === "J&B") {
            this.appYears = null;
            this.onYearSelected(event, null, false);
        }
    }

    onYearSelected(event, year, isSelected = false) {
        this.appYearDashboards = this.allAvailable
            .filter(
                (it) => it.appTitle === this.selectedApp.appTitle && it.flavor === this.selectedApp.flavor && (year ? it.year === year.year : true),
            )
            .map((it) => {
                return {
                    Title: it.dashboard.Title,
                    Id: it.dashboard.Id,
                    isCurrent: it.isCurrent,
                };
            });
        this.selectedYear = year;

        if (isSelected) {
            this.breadcrumbVisibility_DashboardNames = true;
        }

        event?.stopPropagation();
    }

    closeAllSubMenus(force = true) {
        this.allApplications = null;
        this.appYears = null;
        this.appYearDashboards = null;
        this.selectedApp = null;
        this.selectedYear = null;
        if (force) this.toggleAppMenuClickedBy = null;
        if (force || this.toggleAppMenuClickedBy === "app") {
            this.submenuWidth_Application.set(null);
        }
        if (force || this.toggleAppMenuClickedBy === "year" || this.toggleAppMenuClickedBy === "app") {
            this.submenuWidth_Years.set(null);
        }
    }

    setBreadcrumbPosition() {
        const wk_navbar_breadcrumb_dashboard_button = this.navbarBreadcrumbDashboardButton?.()?.nativeElement;
        const wk_navbar_breadcrumb_year_button = this.navbarBreadcrumbYearButton?.()?.nativeElement;

        if (this.isJobsBillingDashboard && this.toggleAppMenuClickedBy === "dashboard") {
            if (wk_navbar_breadcrumb_dashboard_button) {
                const rect = wk_navbar_breadcrumb_dashboard_button.getBoundingClientRect();
                const left = rect.left - 30;
                this.submenuWidth_Application.set(left);
            }
        } else {
            let offsetLeft = 0;
            if (wk_navbar_breadcrumb_year_button && this.toggleAppMenuClickedBy !== "app") {
                const rect = wk_navbar_breadcrumb_year_button.getBoundingClientRect();
                const left = rect.left - 30;
                offsetLeft += left;
                this.submenuWidth_Application.set(left);
            }

            if (wk_navbar_breadcrumb_dashboard_button && this.toggleAppMenuClickedBy === "dashboard") {
                const rect = wk_navbar_breadcrumb_dashboard_button.getBoundingClientRect();
                const left = rect.left - 30 - offsetLeft;
                this.submenuWidth_Years.set(left);
            }
        }
    }

    onDashboardSelected(dashboardId: string) {
        if (this.currentDashboard && dashboardId && dashboardId !== this.currentDashboard.Id) {
            this.shouldTrackDashboardSwitch = true;
        }
    }

    @HostListener("document:click", ["$event"])
    documentClick(event: MouseEvent) {
        const target = event.target as HTMLElement;
        const submenu = target.closest(".wk-navbar__submenu");
        if (!this.toggleAppMenuClicked && !submenu && this.allApplications) {
            this.closeAllSubMenus();
        }
        this.toggleAppMenuClicked = false;
    }
}
